/* eslint-disable max-depth */
/* eslint-disable no-console */
/* eslint-disable no-await-in-loop */
/* eslint-disable max-statements */
import { Box, Button, Center, Container, Flex, Grid, Group, MantineProvider, Modal, Paper, Progress, ScrollArea, Stack, Text } from '@mantine/core';
import { IconArrowLeft, IconArrowRight } from '@tabler/icons-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { saveAs } from 'file-saver';
import { jsPDF } from 'jspdf';
import JSZip from 'jszip';
import moment from 'moment';
import { createRef, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';

import { EORIGIN_DATE_FORMAT } from '@e-origin/shared';

import Logo from '@assets/images/logo192.png';
import {
  UTB_REPORT_DATE_TEXT_X,
  UTB_REPORT_DATE_TEXT_Y,
  UTB_REPORT_FONT_SIZE_SMALL,
  UTB_REPORT_FOOTER_MARGIN,
  UTB_REPORT_FOOTER_POSITION_Y_OFFSET,
  UTB_REPORT_HEADER_TEXT_X,
  UTB_REPORT_HEADER_TEXT_Y,
  UTB_REPORT_LOGO_HEIGHT,
  UTB_REPORT_LOGO_POSITION_X,
  UTB_REPORT_LOGO_WIDTH,
  UTB_REPORT_PDF_MARGIN_BOTTOM,
  UTB_REPORT_PDF_MARGIN_LEFT,
  UTB_REPORT_PDF_MARGIN_TOP,
  UTB_REPORT_PROGRESS_MODAL_CLOSE_DELAY,
  UTB_REPORT_PROGRESS_PERCENTAGE,
} from '@constants';
import { retrieveUTBReportData, useRetrieveUTBReportData } from '@hooks/batch';
import { useDeclarant } from '@hooks/declarant';
import { useRetrieveDeclarantLogo } from '@hooks/declarant/useRetrieveDeclarantLogo';
import { queryClient } from '@hooks/queryClient';
import { useSettings } from '@hooks/useSettings';
import { classes } from '@styles';
import { useTranslation } from './useTranslation';
import { UTBReportTemplate } from './UTBReportTemplate';

interface UTBReportProps {
  batchId: string;
}

export const UTBReport = ({ batchId }: UTBReportProps) => {
  const { data: settings } = useSettings();
  const [page, setPage] = useState(0);
  const { data: reportData } = useRetrieveUTBReportData(batchId, page);
  const [needsPagination, setNeedsPagination] = useState(false);
  const { t } = useTranslation();
  const { data: declarant } = useDeclarant();

  const [templateRefs, setTemplateRefs] = useState(reportData?.groups ? reportData.groups.map(() => createRef()) : []);
  const [isDownloading, setIsDownloading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedGroup, setSelectedGroup] = useState(null);

  const { data: declarantLogo } = useRetrieveDeclarantLogo(declarant?._id, declarant?.logo ? true : false);

  useEffect(() => {
    if (reportData?.totalLRNs) {
      setTemplateRefs(reportData.groups.map(() => createRef()));
      setNeedsPagination(reportData.totalPages > 1);

      if (reportData.groups.length > 0) {
        setSelectedGroup(reportData.groups[0]);
      }
    }
  }, [reportData]);

  const setHeader = (doc: jsPDF, pageCount: number) => {
    doc.setPage(pageCount);
    doc.setFontSize(UTB_REPORT_FONT_SIZE_SMALL);

    doc.addImage(declarantLogo ? declarantLogo : Logo, 'PNG', UTB_REPORT_LOGO_POSITION_X, 1, UTB_REPORT_LOGO_WIDTH, UTB_REPORT_LOGO_HEIGHT);

    doc.text([declarant.name, declarant.identificationNumber].filter((val) => !!val).join(' - '), UTB_REPORT_HEADER_TEXT_X, UTB_REPORT_HEADER_TEXT_Y);

    doc.text(t('HEADER.DATE', { date: moment().format(EORIGIN_DATE_FORMAT), time: moment().format('HH:mm') }), UTB_REPORT_DATE_TEXT_X, UTB_REPORT_DATE_TEXT_Y, {
      align: 'right',
    });
  };

  const setFooter = (doc: jsPDF, pageCount: number) => {
    doc.setPage(pageCount);
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const footerHeight = 10;
    const footerText = `Page ${pageCount} of ${pageCount}`;
    const textWidth = doc.getTextWidth(footerText);
    const x = pageWidth - textWidth - UTB_REPORT_FOOTER_MARGIN;
    const y = pageHeight - footerHeight + UTB_REPORT_FOOTER_POSITION_Y_OFFSET; // Adjust y position to be above the footer
    doc.setFontSize(UTB_REPORT_FONT_SIZE_SMALL);
    doc.text(footerText, x, y);
  };

  const downloadSinglePDF = async (ref) => {
    if (!ref.current) return null;

    try {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      return new Promise<Blob>((resolve, reject) => {
        pdf.html(ref.current, {
          callback: function (doc) {
            try {
              const pageCount = (doc.internal as any).getNumberOfPages();
              for (let i = 1; i <= pageCount; i++) {
                setHeader(doc, i);
                setFooter(doc, i);
              }

              const blob = doc.output('blob');
              resolve(blob);
            } catch (error) {
              if (error instanceof Error) {
                reject(error);
              }
            }
          },
          margin: [UTB_REPORT_PDF_MARGIN_TOP, UTB_REPORT_PDF_MARGIN_LEFT, UTB_REPORT_PDF_MARGIN_BOTTOM, 0],
          x: 10,
          y: 0,
          width: 190, // A4 width minus margins
          windowWidth: ref.current.offsetWidth,
          autoPaging: 'text',
          html2canvas: {
            useCORS: true,
          },
        });
      });
    } catch (err) {
      console.error('Error generating PDF:', err);
      throw err;
    }
  };

  const handleBatchDownload = async () => {
    if (!reportData?.totalLRNs) return;

    setIsDownloading(true);
    setProgress(0);
    setShowProgressModal(true);
    setError(null);

    const { root, container } = createHiddenContainer();

    try {
      for (let i = 0; i < reportData.totalPages; i++) {
        const pageReportData = await retrieveUTBReportData(batchId, i);
        const zip = new JSZip();
        const pageGroups = pageReportData.groups;

        for (let j = 0; j < pageGroups.length; j++) {
          const data = pageGroups[j];
          const ref = createRef();

          try {
            await renderLRNPage(root, ref, data, declarant);
            const pdfBlob = await downloadSinglePDF(ref);

            if (pdfBlob) zip.file(`declaration_${data.lrn}.pdf`, pdfBlob);
          } catch (err) {
            console.error(`Error generating PDF for declaration ${data.lrn}:`, err);
          }

          setProgress(Math.round(((i * 10 + j + 1) * UTB_REPORT_PROGRESS_PERCENTAGE) / reportData.totalLRNs));
        }
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        saveAs(zipBlob, `customs_declarations${needsPagination ? '_page_' + (i + 1) : ''}.zip`);
      }
    } catch (error) {
      console.error('Error generating PDFs:', error);
      setError('Error generating PDFs. Please try again.');
    } finally {
      setIsDownloading(false);
      root.unmount();
      container.remove();
      if (!error) {
        setTimeout(() => setShowProgressModal(false), UTB_REPORT_PROGRESS_MODAL_CLOSE_DELAY);
      }
    }
  };

  const createHiddenContainer = () => {
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.width = '240mm';
    document.body.appendChild(container);
    const root = createRoot(container);

    return { root, container };
  };

  const renderLRNPage = async (root, ref, group, declarant) => {
    flushSync(() => {
      root.render(
        <QueryClientProvider client={queryClient}>
          <MantineProvider>
            <UTBReportTemplate
              declarant={declarant}
              utbDeclarant={reportData.declarant}
              representative={reportData.representative}
              importer={group.importer}
              group={group}
              t={t}
              country={settings.country}
              ref={ref}
            />
          </MantineProvider>
        </QueryClientProvider>,
      );
    });

    await new Promise((r) => requestAnimationFrame(r));
  };

  const handleSingleDownload = async (data, index) => {
    setIsDownloading(true);
    setError(null);

    try {
      const pdfBlob = await downloadSinglePDF(templateRefs[index]);
      if (pdfBlob) {
        saveAs(pdfBlob, `declaration_${data.lrn}.pdf`);
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      setError('Error generating PDF. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  if (!reportData) {
    return (
      <Center mih="80vh" mah="80vh">
        <Text>Loading report data...</Text>
      </Center>
    );
  }

  return (
    <Container fluid mih="80vh" mah="80vh">
      {error && (
        <Paper p="md" mb="md" withBorder bg={'#ffe6e6'}>
          <Text c="red">{error}</Text>
        </Paper>
      )}

      <Grid>
        <Grid.Col span={3}>
          <Paper withBorder p="md" radius="md" h="100%">
            <Group mb="md" justify="apart">
              <Button onClick={handleBatchDownload} disabled={isDownloading} loading={isDownloading} size="sm">
                Download ZIP
              </Button>
            </Group>

            {needsPagination && (
              <Flex justify="space-between" align="center" mb="md">
                <Button type="button" variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 0}>
                  <IconArrowLeft />
                </Button>
                {`${page + 1}/${reportData.totalPages}`}
                <Button type="button" variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page === reportData.totalPages - 1}>
                  <IconArrowRight />
                </Button>
              </Flex>
            )}

            <ScrollArea offsetScrollbars="y" h="calc(80vh - 160px)">
              <Stack gap="md">
                {reportData.groups.map((groups) => (
                  <Paper
                    key={groups.lrn}
                    withBorder
                    p="md"
                    className={classes.cursorPointer}
                    bg={selectedGroup?.lrn === groups.lrn ? '#f0f0f0' : 'white'}
                    onClick={() => setSelectedGroup(groups)}
                  >
                    <Group justify="apart">
                      <Group>
                        <div>
                          <Text size="sm">LRN: {groups.lrn}</Text>
                        </div>
                      </Group>
                    </Group>
                  </Paper>
                ))}
              </Stack>
            </ScrollArea>
          </Paper>
        </Grid.Col>

        <Grid.Col span={9}>
          <ScrollArea offsetScrollbars="y" h="80vh">
            <Paper withBorder p="md" radius="md">
              {selectedGroup ? (
                <>
                  <Box>
                    <Group justify="apart" mb="md">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const index = reportData.groups.findIndex((d) => d.lrn === selectedGroup.lrn);
                          if (index !== -1) {
                            handleSingleDownload(selectedGroup, index);
                          }
                        }}
                        disabled={isDownloading}
                      >
                        Download PDF
                      </Button>
                    </Group>
                  </Box>
                  <Box>
                    {reportData.groups.map((group, groupIdx) => {
                      const isSelected = selectedGroup?.lrn === group.lrn;
                      return (
                        <div key={group.lrn} style={isSelected ? { position: 'relative' } : { position: 'absolute', left: '-9999px', top: 0 }}>
                          <UTBReportTemplate
                            declarant={declarant}
                            utbDeclarant={reportData.declarant}
                            representative={reportData.representative}
                            importer={group.importer}
                            group={group}
                            t={t}
                            country={settings.country}
                            ref={templateRefs[groupIdx]}
                          />
                        </div>
                      );
                    })}
                  </Box>
                </>
              ) : (
                <Text>Select a declaration from the sidebar</Text>
              )}
            </Paper>
          </ScrollArea>
        </Grid.Col>
      </Grid>

      <Modal
        opened={showProgressModal}
        onClose={() => !isDownloading && !error && setShowProgressModal(false)}
        title={error ? 'Error' : 'Generating PDFs'}
        closeOnClickOutside={!isDownloading && !error}
        closeOnEscape={!isDownloading && !error}
        withCloseButton={!isDownloading || !!error}
        centered
        zIndex={9999}
      >
        <Box py="md">
          {error ? (
            <Text c="red">{error}</Text>
          ) : (
            <>
              <Progress value={progress} mb="md" size="xl" radius="xl" />
              <Text ta="center">{progress}% Complete</Text>
            </>
          )}

          {error && (
            <Button fullWidth mt="md" onClick={() => setShowProgressModal(false)}>
              Close
            </Button>
          )}
        </Box>
      </Modal>
    </Container>
  );
};
